import { subDays, startOfDay, format, parseISO } from "date-fns";
import type { InstallPeriodType, InstallRecordType, Granularity } from "@intelligentlilli/api-layer";
import theme from "../../Styles/theme.scss";

// --- Presets ---

export type PresetKey =
  | "last7days"
  | "last4weeks"
  | "last3months"
  | "last6months"
  | "last12months"
  | "custom";


export interface Preset {
  key: PresetKey;
  label: string;
  defaultGranularity: Granularity;
  days: number;
}

export const PRESETS: Preset[] = [
  { key: "last7days", label: "Last 7 days", defaultGranularity: "D", days: 7 },
  { key: "last4weeks", label: "Last 4 weeks", defaultGranularity: "W", days: 31 },
  { key: "last3months", label: "Last 3 months", defaultGranularity: "M", days: 90 },
  { key: "last6months", label: "Last 6 months", defaultGranularity: "M", days: 180 },
  { key: "last12months", label: "Last 12 months", defaultGranularity: "M", days: 365 },
  { key: "custom", label: "Custom range", defaultGranularity: "D", days: 0 },
];

export const PRESET_OPTIONS = PRESETS.map((p) => ({
  label: p.label,
  value: p.key,
}));

// --- Date range computation ---

// The API's is-utc-timestamp? validation requires UTC timestamps without
// milliseconds, e.g. "2026-08-13T14:22:31Z" (toISOString() emits ".000Z").
export const toUtcTimestamp = (date: Date): string =>
  date.toISOString().replace(/\.\d{3}Z$/, "Z");

export const getPresetDateRange = (
  presetKey: PresetKey,
  customRange?: { startDate: Date; endDate: Date } | null
): { startTime: string; endTime: string } => {
  const now = new Date();
  const endTime = toUtcTimestamp(now);

  if (presetKey === "custom" && customRange) {
    return {
      startTime: toUtcTimestamp(startOfDay(customRange.startDate)),
      endTime: toUtcTimestamp(customRange.endDate),
    };
  }

  const preset = PRESETS.find((p) => p.key === presetKey);
  const days = preset?.days || 7;
  const startTime = toUtcTimestamp(startOfDay(subDays(now, days)));

  return { startTime, endTime };
};

// --- Cache key ---

export const getCacheKey = (
  orgId: number,
  granularity: Granularity,
  startTime: string,
  endTime: string
): string => `${orgId}-${granularity}-${startTime}-${endTime}`;

// --- Data transforms ---

export const flattenFailedRecords = (
  periods: InstallPeriodType[]
): InstallRecordType[] => {
  const records = periods.flatMap((period) => period.records.failed);
  return records.sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
};

// --- Sorting ---

type SortField = "userName" | "timestamp" | "reason";
type SortDirection = "asc" | "desc";

export const sortRecords = (
  records: InstallRecordType[],
  field: SortField,
  direction: SortDirection
): InstallRecordType[] => {
  const sorted = [...records].sort((a, b) => {
    const valA = a[field] ?? "";
    const valB = b[field] ?? "";

    if (field === "timestamp") {
      return new Date(valA).getTime() - new Date(valB).getTime();
    }

    return String(valA).toLowerCase().localeCompare(String(valB).toLowerCase());
  });

  return direction === "desc" ? sorted.reverse() : sorted;
};

// --- Badge colors ---

interface BadgeStyle {
  backgroundColor: string;
  color: string;
}

const REASON_STYLES: Record<string, BadgeStyle> = {
  refused: { backgroundColor: theme.risk2, color: theme.risk6 },
  unavailable: { backgroundColor: theme.unexpected1, color: theme.neutral7 },
  unsafe: { backgroundColor: theme.risk1, color: theme.risk5 },
  logistics: { backgroundColor: theme.primary1, color: theme.primary6 },
};

const UNKNOWN_STYLE: BadgeStyle = {
  backgroundColor: theme.neutral2,
  color: theme.neutral6,
};

export const getReasonBadgeStyle = (reason: string | null): BadgeStyle => {
  if (!reason) return UNKNOWN_STYLE;
  return REASON_STYLES[reason.toLowerCase()] || UNKNOWN_STYLE;
};

export const getReasonLabel = (reason: string | null): string => {
  if (!reason) return "Unknown";
  return reason.charAt(0).toUpperCase() + reason.slice(1).toLowerCase();
};

// --- Safe date formatting ---

export const safeFormatISO = (isoString: string, formatStr: string): string => {
  try {
    const date = parseISO(isoString);
    if (isNaN(date.getTime())) return isoString;
    return format(date, formatStr);
  } catch {
    return isoString;
  }
};

// --- Chart label formatting ---

export const formatPeriodLabel = (
  period: InstallPeriodType,
  granularity: Granularity
): string => {
  switch (granularity) {
    case "D":
      return safeFormatISO(period.period.start, "EEE d");
    case "W":
      return `W/C ${safeFormatISO(period.period.start, "d MMM")}`;
    case "M":
      return safeFormatISO(period.period.start, "MMM yyyy");
    default:
      return period.key;
  }
};

// --- Range label formatting ---

export const formatRangeLabel = (
  range: { start: string; end: string },
  granularity: Granularity
): string => {
  switch (granularity) {
    case "D":
      return `${safeFormatISO(range.start, "d MMM yyyy")} – ${safeFormatISO(range.end, "d MMM yyyy")}`;
    case "W":
      return `${safeFormatISO(range.start, "d MMM")} – ${safeFormatISO(range.end, "d MMM yyyy")}`;
    case "M":
      return `${safeFormatISO(range.start, "MMM yyyy")} – ${safeFormatISO(range.end, "MMM yyyy")}`;
    default:
      return `${safeFormatISO(range.start, "d MMM yyyy")} – ${safeFormatISO(range.end, "d MMM yyyy")}`;
  }
};
